import { z } from "zod";
import { accountTokenSchema } from "./fields";

export const legalNameSchema = z
  .string()
  .trim()
  .min(2, "Type your full legal name to sign.")
  .max(100, "Name must be at most 100 characters.");

/**
 * What the browser sends when someone signs: the version and fingerprint of the exact
 * text shown to them, their typed name, and the unchecked-by-default agreement box.
 */
export const agreementSignatureSchema = z.object({
  version: z.string().min(1),
  textSha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/, "Invalid agreement fingerprint."),
  signedName: legalNameSchema,
  agreed: z.literal(true, { error: "Check the box to agree." }),
});
export type AgreementSignature = z.infer<typeof agreementSignatureSchema>;

/** A guest signing the User Agreement through a trip's guest waiver link. */
export const guestSignatureSchema = agreementSignatureSchema.extend({
  token: accountTokenSchema,
  email: z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .pipe(z.email({ error: "Enter a valid email so we can send you a copy." })),
  isAdult: z.literal(true, {
    error: "Only adults can sign. A parent or guardian signs for a minor.",
  }),
  minorNames: z
    .string()
    .trim()
    .max(300, "Keep this under 300 characters.")
    .optional()
    .transform((v) => (v ? v : undefined)),
});
export type GuestSignature = z.infer<typeof guestSignatureSchema>;

/** What the public guest page may show about a trip before someone signs. */
export const guestTripSchema = z.discriminatedUnion("valid", [
  z.object({
    valid: z.literal(true),
    boatName: z.string(),
    borrowerName: z.string(),
    startDate: z.string(),
    endDate: z.string(),
  }),
  z.object({ valid: z.literal(false) }),
]);
export type GuestTrip = z.infer<typeof guestTripSchema>;

/** Guest waivers collected for one reservation, shown to its borrower and admins. */
export const guestWaiversSchema = z.object({
  /** Token for the trip's guest waiver link. */
  token: z.string(),
  signatures: z.array(
    z.object({
      signedName: z.string(),
      signedAt: z.date(),
      minorNames: z.string().optional(),
    })
  ),
});
export type GuestWaivers = z.infer<typeof guestWaiversSchema>;
